import React, { useState, FormEvent } from "react";
import { Box, TextField, Button, Typography } from "@mui/material";
import { getTranslation } from "../locales";

interface PinLandingPageProps {
  locale: string;
  surveyTitle: string;
  onPinSubmit: (pin: string) => Promise<boolean>;
}

const PinLandingPage: React.FC<PinLandingPageProps> = ({ locale, surveyTitle, onPinSubmit }) => {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const t = getTranslation(locale);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!pin.trim()) {
      setError(true);
      return;
    }

    setError(false);
    setIsSubmitting(true);

    try {
      const isValid = await onPinSubmit(pin.trim());
      
      if (!isValid) {
        setError(true);
        setPin("");
      }
    } catch (err) {
      setError(true);
      setPin("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPin(e.target.value);
    if (error) {
      setError(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        px: 3,
        py: 6,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "550px",
          textAlign: "center",
        }}
      >
        {/* Survey Title */}
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 500,
            mb: 6,
            color: "#333",
            fontSize: { xs: "1.5rem", sm: "2rem" },
          }}
        >
          {surveyTitle}
        </Typography>

        {/* Welcome Message */}
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 400,
            mb: 2,
            color: "#555",
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
          }}
        >
          {t.pinWelcome}
        </Typography>

        {/* Instructions */}
        <Typography
          variant="body1"
          sx={{
            color: "#666",
            mb: 5,
            px: { xs: 0, sm: 2 },
            lineHeight: 1.6,
          }}
        >
          {t.pinInstructions}
        </Typography>

        {/* PIN Form */}
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 1.5,
                color: "#E87424",
                fontSize: "14px",
                fontWeight: 500,
                textAlign: "center",
              }}
            >
              {t.enterYourPin}
            </Typography>
            <Box
              sx={{
                maxWidth: "400px",
                mx: "auto",
              }}
            >
              <TextField
                fullWidth
                value={pin}
                onChange={handlePinChange}
                placeholder={t.pinPlaceholder}
                error={error}
                helperText={error ? t.pinError : ""}
                disabled={isSubmitting}
                autoFocus
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: error ? "#FEF5F5" : "#F5F5F5",
                    "& fieldset": {
                      borderColor: error ? "#d32f2f" : "#D0D0D0",
                      borderWidth: "2px",
                    },
                    "&:hover fieldset": {
                      borderColor: error ? "#d32f2f" : "#999",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: error ? "#d32f2f" : "#E87424",
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: "20px 18px",
                    fontSize: "18px",
                    textAlign: "center",
                    letterSpacing: "0.5px",
                    fontWeight: 500,
                  },
                  "& .MuiFormHelperText-root": {
                    textAlign: "center",
                    mx: 0,
                  },
                }}
              />
            </Box>
          </Box>

          {/* Submit Button */}
          <Box
            sx={{
              maxWidth: "400px",
              mx: "auto",
            }}
          >
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSubmitting}
              sx={{
                backgroundColor: "#E87424",
                color: "#FFFFFF",
                border: "none",
                padding: "14px 24px",
                fontSize: "16px",
                fontWeight: 500,
                textTransform: "uppercase",
                boxShadow: "none",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#D06520",
                  boxShadow: "none",
                },
                "&:disabled": {
                  backgroundColor: "#BDBDBD",
                  color: "#FFFFFF",
                },
              }}
            >
              {isSubmitting ? t.submitting : t.submit}
            </Button>
          </Box>
        </form>

        {/* Help Text */}
        <Typography
          variant="body2"
          sx={{
            mt: 5,
            mb: 2,
            color: "#666",
            fontSize: "14px",
          }}
        >
          {t.pinNeedHelp}
        </Typography>

        {/* Powered By Footer */}
        <Typography
          variant="caption"
          sx={{
            color: "#999",
            fontSize: "12px",
          }}
        >
          {t.poweredByTalentMap}
        </Typography>
      </Box>
    </Box>
  );
};

export default PinLandingPage;
